/**
 * API Configuration
 * Handles environment-aware API endpoint configuration
 */

// Determine the API base URL based on environment
function getApiBaseUrl(): string {
  // Check for explicit API URL from environment variable first
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;
  if (envApiUrl) {
    return envApiUrl;
  }

  // If we're running in development (localhost), use local API
  if (globalThis.window?.location.hostname === "localhost") {
    return "http://localhost:3333";
  }

  // For production and staging, use the deployed API
  return "https://api.lumi.theforge.space";
}

export const API_BASE_URL = getApiBaseUrl();

// Create a helper function for making API calls with automatic token refresh
export async function apiCall(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const makeRequest = async (token: string | null): Promise<Response> => {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // Add Authorization header if token exists
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Get JWT token from localStorage for authenticated requests
  const token = globalThis.window === undefined ? null : globalThis.window.localStorage.getItem("lumi-access-token");

  // Make initial request
  let response = await makeRequest(token);

  // If we get 401 and have a refresh token, attempt to refresh and retry
  if (response.status === 401 && globalThis.window !== undefined) {
    console.log("🔄 Got 401, attempting token refresh for endpoint:", endpoint);
    const refreshToken = globalThis.window.localStorage.getItem("lumi-refresh-token");

    if (refreshToken) {
      try {
        console.log("🔄 Calling refresh endpoint...");
        // Attempt to refresh the access token
        const refreshResponse = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });

        console.log("🔄 Refresh response status:", refreshResponse.status);
        if (refreshResponse.ok) {
          const { accessToken } = (await refreshResponse.json()) as { accessToken: string };
          console.log("✅ Got new access token, retrying original request");

          // Store new access token
          globalThis.window.localStorage.setItem("lumi-access-token", accessToken);

          // Retry original request with new token
          response = await makeRequest(accessToken);
          console.log("✅ Retry response status:", response.status);
        } else {
          console.error("❌ Refresh failed with status:", refreshResponse.status);
          // Refresh token is invalid/expired - clear auth and redirect to login
          console.log("🔄 Clearing invalid tokens and redirecting to login...");
          globalThis.window.localStorage.removeItem("lumi-access-token");
          globalThis.window.localStorage.removeItem("lumi-refresh-token");
          globalThis.window.localStorage.removeItem("lumi-user");
          globalThis.window.location.href = "/sign-in";
        }
      } catch (error) {
        console.error("❌ Token refresh failed during API call:", error);
        // If refresh fails, return the original 401 response
      }
    } else {
      console.log("❌ No refresh token found in localStorage");
    }
  }

  return response;
}

// Debug helper
if (globalThis.window !== undefined) {
  console.log("🔗 API Base URL:", API_BASE_URL);
}
